/**
 * PageBannerProvider — lets routed pages surface a short-lived status strip in
 * the global banner band below the app header (same slot as SyncBanner).
 */
import React from 'react';

export interface PageBannerState {
  message: string;
  tone?: 'info' | 'warning';
}

interface PageBannerContextValue {
  banner: PageBannerState | null;
  setBanner: (banner: PageBannerState | null) => void;
}

export const PageBannerContext = React.createContext<PageBannerContextValue>({
  banner: null,
  setBanner: () => {},
});

export function PageBannerProvider({ children }: { children: React.ReactNode }) {
  const [banner, setBanner] = React.useState<PageBannerState | null>(null);
  const value = React.useMemo(() => ({ banner, setBanner }), [banner]);
  return <PageBannerContext.Provider value={value}>{children}</PageBannerContext.Provider>;
}

/** Register a page-level banner for the current route; cleared on unmount or when message is null. */
export function usePageBanner(banner: PageBannerState | null) {
  const { setBanner } = React.useContext(PageBannerContext);
  const message = banner?.message ?? null;
  const tone = banner?.tone ?? 'info';

  React.useEffect(() => {
    if (message) {
      setBanner({ message, tone });
    } else {
      setBanner(null);
    }
    return () => setBanner(null);
  }, [message, tone, setBanner]);
}
